import { useMemo, useCallback } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import config from 'constants/config'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import setRegionCookie from 'cookies/setRegionCookie'
import { Status, setGeoRedirect } from 'storage/geo'

interface Props {
  checkoutCurrency: string
  mismatchedCurrencies: Array<string>
}

function CurrencyMismatchWarning({ checkoutCurrency, mismatchedCurrencies }: Props) {
  const intl = useIntl()

  const redirectRegions = useMemo(() => {
    return mismatchedCurrencies.reduce<Record<string, Array<App.RegionSummary>>>((acc, currency) => {
      acc[currency] = config.regions.filter(region => region.currencyCode === currency)
      return acc
    }, {})
  }, [mismatchedCurrencies])

  const onRegionLinkClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.button === 0 || e.button === 1) {
      const regionCode = e.currentTarget.dataset.regioncode ?? ''
      setRegionCookie(regionCode)
      setGeoRedirect(Status.redirected)
    }
  }, [])

  const heading = intl.formatMessage({
    id: 'QdkD4F35TPIB7wC3z2Bd',
    defaultMessage: 'Currency mismatch',
    description: 'Section heading for currency mismatch warning banner',
  })
  const currencyMismatchLabel = intl.formatMessage(
    {
      id: '7tjiAoud9EoMDWO76WCh',
      defaultMessage: '{count, plural, one {a currency that does not} other {currencies that do not}}',
      description: 'Currency mismatch phrase describing one or more redeemed credit currencies',
    },
    { count: mismatchedCurrencies.length },
  )

  return (
    <MessageBanner kind="warning" heading={heading}>
      <VerticalSpacer gap={8}>
        <div>
          <FormattedMessage
            id="lwJ4MatZ0DyexReS_jOd"
            defaultMessage="You have redeemed credit in {currencyMismatchLabel} match the current checkout currency (<b>{checkoutCurrency}</b>)."
            description="Warning explaining that redeemed gift card credit uses currencies that differ from the current checkout currency"
            values={{
              currencyMismatchLabel,
              checkoutCurrency,
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        </div>
        {mismatchedCurrencies.map(currency => {
          const regionCountLabel = intl.formatMessage(
            {
              id: 'eu3yyEOLBNdk5YXtrrnH',
              defaultMessage: '{regionCount, plural, one {region} other {regions}}',
              description: 'Region count label for redeemed credit currency availability',
            },
            { regionCount: redirectRegions[currency].length },
          )

          return (
            <React.Fragment key={currency}>
              <div>
                <FormattedMessage
                  id="aRAw1_5m-ZH0vPWAbRh2"
                  defaultMessage="Redeemed credit for <b>{currency}</b> will be available in the following {regionCountLabel}:"
                  description="Message introducing the list of regions where credit for a specific redeemed currency can be used"
                  values={{
                    currency,
                    regionCountLabel,
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </div>
              <ul>
                {redirectRegions[currency].map(region => (
                  <li key={region.code}>
                    <TextLink
                      href={`/${region.code.toLowerCase()}`}
                      data-regioncode={region.code}
                      onClick={onRegionLinkClick}
                      onAuxClick={onRegionLinkClick}
                    >
                      {region.name}
                    </TextLink>
                  </li>
                ))}
              </ul>
            </React.Fragment>
          )
        })}
      </VerticalSpacer>
    </MessageBanner>
  )
}

export default CurrencyMismatchWarning
